import {
  addMinutes as dateFnsAddMinutes,
  format as dateFnsFormat,
} from 'date-fns';
import { format as dateFnsTzFormat, toZonedTime } from 'date-fns-tz';

export * from './countryDateFormats';

export interface DateParts {
  weekday: string;
  month: string;
  day: string;
  year: string;
}

const DATETIME_FORMATS: { [key: string]: string } = {
  date: 'd', // 3
  shortWeekday: 'EEE', // Thu
  longWeekday: 'EEEE', // Thursday
  shortMonth: 'MMM', // Oct
  longMonth: 'MMMM', // October
  longYear: 'yyyy', // 2022
  shortMonthAndDate: 'MMM dd', // Oct 03
  shortMonthAndLongYear: 'MMM yyyy', // Oct 2022
  shortDayOfWeekAndDate: 'EEE d', // Fri 7
  hoursAndMinutesWithAMPM: 'h:mma', // 7:00PM
  shortMonthDateAndYear: 'MMM d, yyyy', // Oct 7, 2022
  shortMonthDateAndYearHoursAndMinute: 'MMM d, yyyy h:mm a', // Oct 7, 2022 7:00 PM
  yearMonthDate: 'yyyy-MM-dd', // 2022-09-07
  hoursAndMinutesWithSpacedAMPM: 'h:mm a', // 7:00 PM
  hoursAndMinutesWithoutAMPM: 'HH:mm', // 07:00
  hoursMinutesSeconds: 'HH:mm:ss', // 19:00:00
  longMonthDateAndYear: 'MMMM dd, yyyy', // October 07, 2022
  shortDateAndLongMonth: 'd MMMM', // 3 October (UK/global)
  longMonthAndShortDate: 'MMMM d', // October 3 (US/CA)
  shortDateLongMonthAndYear: 'd MMMM, yyyy', // 3 October, 2022 (UK/global)
  fullMonthDateWithOrdinalAndYear: 'MMMM do, yyyy', // October 7th, 2022
  shortWeekdayDateWithOrdinalAndShortMonth: 'EEE do MMM', // Fri 7th Nov
  shortWeekdayShortMonthDateAndYear: 'EEE, MMM d, yyyy', // Fri, Nov 7, 2022
  shortWeekdayDateShortMonth: 'EEE d MMM', // Fri 7 Nov
  longWeekdayShortMonthDateAndYear: 'EEEE, MMM d, yyyy', // Friday, Nov 7, 2022
  longWeekDayDateWithOrdinalAndLongMonth: 'EEEE do MMMM', // Friday 7th November
  longWeekdayDateLongMonthAndYear: 'EEEE, dd MMMM yyyy', // Friday, 07 November 2022
  longWeekdayDateLongMonth: 'EEEE d MMMM', // Friday 7 November
  longWeekdayCommaDateLongMonth: 'EEEE, d MMMM', // Friday, 7 November (UK/global)
  longWeekdayCommaLongMonthDate: 'EEEE, MMMM d', // Friday, November 7 (US/CA)
  yearMonthDateHoursMinutesAndSeconds: 'yyyy-MM-dd HH:mm:ss', // 2022-11-07 19:00:00
  longWeekdayDateWithOrdinalLongMonthYear: 'EEEE do MMMM yyyy', // Friday 7th November 2022
  shortWeekdayShortMonthDateHoursAndMinutesWithTimezone: 'EEE, MMM d, h:mm a', // Fri, Nov 7, 7:00 PM
  longMonthDateHoursAndMinutes: 'MMMM d, yyyy, HH:mm', // November 7, 2022, 15:28
  longMonthDateHoursAndMinutesWithAMPM: 'MMMM d, yyyy, HH:mma', // November 7, 2022, 15:28PM
  shortMonthDateYearHoursAndMinutesWithSpacedAMPMWithTimezone:
    'MMM d, yyyy, h:mm a zzz', // Nov 7, 2022, 7:00 PM GMT
  longWeekdayDateWithOrdinalFullMonthHoursMinutesWithAMPM: 'EEEE do MMMM h:mma', // Friday 7th November 7:00PM
  shortWeekdayDateWithOrdinalShortMonthHoursMinutesWithAMPM: 'EEE do MMM h:mma', // Fri 7th Nov 7:00PM
  longMonthDateAndYearHoursMinutesWithAMPM: 'MMMM dd, yyyy h:mma', // October 07, 2022 7:00PM
  longWeekdayLongMonthDateAndYear: 'EEEE, MMMM d, yyyy', // Friday, November 7, 2022
  yearMonthAndDateWithHoursMinutesSecondsAMPMWithTimezone:
    'yyyy-MM-dd HH:mm:ss zzz', // 2022-11-07 07:00:00 GMT
};

// Given a timezone string, ex: 'America/New_York'
// Returns a number with the current year in that local timezone
export const getCurrentYearInTimeZone = (timeZone: string): number => {
  return Number.parseInt(
    dateFormatter(new Date(Date.now()), 'longYear', timeZone)
  );
};

// Given a UTC datetime string, ex: 2022-09-26T20:00:00Z
// Returns the individual parts of the date:
// weekday, day, month, year
// Note: the input date will be treated as UTC
// Output will look similar to:
// [
//   {type: 'weekday', value: 'Thu'},
//   {type: 'literal', value: ', '},
//   {type: 'day', value: '22'},
//   {type: 'literal', value: ' '},
//   {type: 'month', value: 'Sept'},
//   {type: 'literal', value: ' '},
//   {type: 'year', value: '2022'},
// ]
const dateFormatToParts = (date: Date | string, utc = true) => {
  const formatOptions: any = {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: utc ? 'UTC' : undefined,
  };

  return new Intl.DateTimeFormat('en-GB', formatOptions).formatToParts(
    new Date(date)
  );
};

// See DATETIME_FORMATS
// Given a datetime string, e.g. '2022-09-26T20:00:00Z'
// A predefined formatting string, e.g. 'hoursAndMinutesWithAMPM', or unicode format, e.g. 'yyyy'
// And an optional timezone, e.g. 'America/New_York'
// Returns the formatted datetime
// If timezone arg is passed, it returns the localized and formatted datetime
// If timezone arg is not passed it only formats the datetime
export const dateFormatter = (
  date: Date | string,
  formatOption: string,
  timeZone?: string
) => {
  const unicodeFormat = DATETIME_FORMATS.hasOwnProperty(formatOption)
    ? DATETIME_FORMATS[formatOption]
    : formatOption;

  if (timeZone) {
    const zonedDateTime = toZonedTime(new Date(date), timeZone);
    return dateFnsTzFormat(zonedDateTime, unicodeFormat, { timeZone });
  }

  let d = new Date(date);
  const offset = d.getTimezoneOffset();
  const adjust = dateFnsAddMinutes(d, offset);
  return dateFnsFormat(adjust, unicodeFormat);
};

export const getDateParts = (date: Date | string, utc = true): DateParts => {
  const dateParts = dateFormatToParts(date, utc);

  return {
    weekday: dateParts[0].value,
    day: dateParts[2].value,
    month: dateParts[4].value,
    year: dateParts[6].value,
  };
};

// Given a datetime string, e.g. '2022-11-16T20:00:00Z'
// Returns e.g. '2022-11-16' for passing to backend
export const getDateSubmitVariable = (dateStr?: string) =>
  dateStr && dateStr.split('T')[0];

export const getDatesSubmitVariable = (dateStrs?: string[]) => {
  return (
    dateStrs &&
    dateStrs.map((dateStr: string) => getDateSubmitVariable(dateStr))
  );
};

// Given EITHER OF:
// - a full datetime string e.g. '2025-02-16T01:05:00Z'
// - a time string e.g. '19:30:00'
// Returns e.g. '2001-01-01T19:30:00Z' for use by timepicker component
export const getTimeFormInitialValue = (
  timeStr?: string,
  timezone?: string
) => {
  if (!timeStr) {
    return undefined;
  }

  if (timezone) {
    return (
      dateFormatter(
        timeStr,
        'yearMonthDateHoursMinutesAndSeconds',
        timezone
      ).replace(' ', 'T') + 'Z'
    );
  } else {
    return `2001-01-01T${timeStr}Z`;
  }
};

// Given a datetime object returned by timepicker component
// Returns e.g. '19:30:00' for passing to backend
export const getTimeSubmitVariable = (rawTime?: any) => {
  const timeParts = rawTime && String(rawTime).match(/(\d\d:\d\d):\d\d/);
  return timeParts && timeParts[1] && `${timeParts[1]}:00`;
};

// Given a year-month date string e.g. '2026-10'
// Returns year-month string with short month name for display e.g. 'Oct 2026'
export const yearMonthFormatter = (yearMonthRaw?: string) => {
  if (!yearMonthRaw) {
    return '';
  }

  const [year, month] = yearMonthRaw.split('-');
  const date = new Date(Number(year), Number(month) - 1, 1);

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    year: 'numeric',
  }).format(date);
};

// Used to keep frontend in sync with event_date_time_display_service in backend
export const uses12HourClock = (countryCode: any) => {
  const countries = ['US', 'CA'];

  return countries.includes(countryCode);
};

// Takes a timestamp like "2024-11-20T20:03:50Z"
export function isOlderThanThirtyDays(timestamp?: string) {
  if (!timestamp) {
    return false;
  }
  const thirtyDaysInMilliseconds = 30 * 24 * 60 * 60 * 1000;
  const currentTime = Date.now();
  const inputTime = new Date(timestamp).getTime();

  return currentTime - inputTime > thirtyDaysInMilliseconds;
}

// dateStr = date as a string e.g. '2025-11-20'
// timeZone = timezone string, ex: 'America/New_York'
export const isDateTodayOrEarlier = (dateStr: string, timeZone: string) => {
  // Gets e.g. '2025-11-07'
  const zonedCurrentDateStr = dateFormatter(
    new Date(),
    'yearMonthDate',
    timeZone
  );

  return dateStr <= zonedCurrentDateStr;
};

// dateStr = date as a string e.g. '2025-11-20'
// timeZone = timezone string, ex: 'America/New_York'
export const isDateAfterToday = (dateStr: string, timeZone: string) => {
  // Gets e.g. '2025-11-07'
  const zonedCurrentDateStr = dateFormatter(
    new Date(),
    'yearMonthDate',
    timeZone
  );

  return dateStr > zonedCurrentDateStr;
};

// e.g. is it currently:
// - 2026-09-17T10:30:00 in New York
// - 2026-09-18T02:30:00 in UTC
// timeZone = timezone string, ex: 'America/New_York'
export const isCurrentTimeTodayLocalButTomorrowUTC = (timeZone: string) => {
  // Gets e.g. '2026-09-17'
  const zonedCurrentDateStr = dateFormatter(
    new Date(),
    'yearMonthDate',
    timeZone
  );

  // Gets e.g. '2026-09-18'
  const utcCurrentDateStr = dateFormatter(new Date(), 'yearMonthDate', 'UTC');

  return utcCurrentDateStr > zonedCurrentDateStr;
};

// Timestamp = e.g. "2024-11-20T20:03:50Z"
// Returns an integer containing number of minutes until that time -
// if given timestamp is BEFORE now, returns a negative number
export const numMinutesUntilDateTime = (timestamp: string) => {
  // e.g. 1780580991855
  const timestampInMilliseconds = new Date(timestamp).getTime();
  const nowInMilliseconds = Date.now();

  const differenceInMilliseconds = timestampInMilliseconds - nowInMilliseconds;
  const differenceInMinutes = Math.floor(
    differenceInMilliseconds / (1000 * 60)
  );

  return differenceInMinutes;
};

// Timestamp = e.g. "2024-11-20T20:03:50Z"
// Returns true or false
export const isDateTimeInFuture = (timestamp: string) => {
  // e.g. 1780580991855
  const timestampInMilliseconds = new Date(timestamp).getTime();
  const nowInMilliseconds = Date.now();

  return !!(timestampInMilliseconds > nowInMilliseconds);
};

// Timestamp = e.g. "2024-11-20T20:03:50Z"
// Returns true or false -
// if given timestamp is AFTER now, returns FALSE
export const isDateTimeWithinXHoursAgo = (
  timestamp: string,
  numHoursAgo: number
) => {
  // e.g. 1780580991855
  const timestampInMilliseconds = new Date(timestamp).getTime();
  const nowInMilliseconds = Date.now();

  const differenceInMilliseconds = nowInMilliseconds - timestampInMilliseconds;
  const differenceInHours = Math.ceil(
    differenceInMilliseconds / (1000 * 60 * 60)
  );

  return differenceInHours < 0 ? false : !!(differenceInHours <= numHoursAgo);
};
